import type { ChartOptions } from 'chart.js';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faPen, faPlus, faX } from '@fortawesome/pro-regular-svg-icons';
import { getStartOfDay, addDays, set } from '@querodelivery/qd-date-helper';
import {
  array,
  date,
  mixed,
  object,
  string,
  ValidationError,
} from '@/_config/lib/yup';
import { requiredErrorMessage } from '@/_constants/form-messages';
import { colors } from '@/Themes/Colors';

export const MISSION_NAME_MAX_LENGTH = 15;

export const MISSION_TITLE_DEFAULT =
  'Complete entregas durante o período e ganhe bônus!';

export const MISSION_DESCRIPTION_DEFAULT =
  'Quanto mais você entrega, maior o seu ganho. 💰';

export const MISSION_END_BEFORE_START_MESSAGE =
  'A data final deve ser depois da data inicial';

export const MISSION_START_DATE_MIN_MESSAGE =
  'A data inicial deve ser no mínimo o dia seguinte ao atual';

export const getMissionMinStartDate = () =>
  getStartOfDay(addDays(new Date(), 1));

export const MISSION_OBJECTIVE_GREATER_MESSAGE =
  'Deve ser maior que o objetivo anterior';

export const MISSION_REWARD_GREATER_MESSAGE =
  'Deve ser maior que a premiação anterior';

export const MISSION_PERIOD_FIELDS: (
  | 'startDate'
  | 'startTime'
  | 'endDate'
  | 'endTime'
)[] = ['startDate', 'startTime', 'endDate', 'endTime'];

export const MISSION_OBJECTIVE_REWARD_FIELDS: (
  | 'objective01'
  | 'objective02'
  | 'objective03'
  | 'reward01'
  | 'reward02'
  | 'reward03'
)[] = [
  'objective01',
  'objective02',
  'objective03',
  'reward01',
  'reward02',
  'reward03',
];

export enum EMissionType {
  CITY = 'CITY',
  DELIVERYMAN = 'DELIVERYMAN',
}

export const TARGET_AUDIENCE = {
  CITY: EMissionType.CITY,
  DELIVERYMAN: EMissionType.DELIVERYMAN,
};

export type TargetAudience =
  (typeof TARGET_AUDIENCE)[keyof typeof TARGET_AUDIENCE];

export const combineDateAndTime = (dateValue: Date, timeValue: Date) =>
  set(dateValue, {
    hours: timeValue.getHours(),
    minutes: timeValue.getMinutes(),
    seconds: 0,
    milliseconds: 0,
  });

export const buildMissionConditionText = (conditionValue: number) =>
  `Faça ${conditionValue} ou mais entregas e ganhe`;

const isPositiveMoney = (value?: string) => {
  if (!value) return true;

  const numericValue = parseFloat(value.replace(/\./g, '').replace(',', '.'));
  return !Number.isNaN(numericValue) && numericValue > 0;
};

const isPositiveInteger = (value?: string) => {
  if (!value) return true;

  return /^\d+$/.test(value) && Number(value) > 0;
};

const parseMoneyValue = (value?: string) => {
  if (!value) return null;

  const numericValue = parseFloat(value.replace(/\./g, '').replace(',', '.'));
  return Number.isNaN(numericValue) ? null : numericValue;
};

const parseObjectiveValue = (value?: string) => {
  if (!value || !/^\d+$/.test(value)) return null;

  return Number(value);
};

const positiveMoneySchema = string().test(
  'positive-money',
  'O valor deve ser positivo',
  isPositiveMoney,
);

const positiveIntegerSchema = string().test(
  'positive-integer',
  'Informe uma quantidade válida de entregas',
  isPositiveInteger,
);

export const CREATE_MISSION_FORM_SCHEMA = object({
  startDate: date()
    .required()
    .test('start-date-after-today', MISSION_START_DATE_MIN_MESSAGE, value => {
      if (!value) return true;

      return getStartOfDay(value) >= getMissionMinStartDate();
    }),
  startTime: date().required(),
  endDate: date().required(),
  endTime: date().required(),
  targetAudience: string()
    .oneOf([EMissionType.CITY, EMissionType.DELIVERYMAN])
    .required(),
  cityIds: array()
    .of(string().required())
    .when('targetAudience', {
      is: EMissionType.CITY,
      then: schema => schema.min(1, requiredErrorMessage).required(),
      otherwise: schema => schema.optional(),
    }),
  deliverymanIds: array().of(string().required()).default([]),
  deliverymanCsv: mixed<File | null>().nullable().default(null),
  missionName: string().required().max(MISSION_NAME_MAX_LENGTH),
  budget: positiveMoneySchema.required(),
  objective01: positiveIntegerSchema.required(),
  objective02: positiveIntegerSchema.optional(),
  objective03: positiveIntegerSchema.optional(),
  reward01: positiveMoneySchema.required(),
  reward02: positiveMoneySchema.optional(),
  reward03: positiveMoneySchema.optional(),
  missionTitle: string().optional(),
  missionDescription: string().optional(),
})
  .test(
    'deliveryman-ids-or-csv',
    requiredErrorMessage,
    function validateDeliverymanAudience(values) {
      if (this.options.context?.isEdit) {
        return true;
      }

      if (values?.targetAudience !== EMissionType.DELIVERYMAN) {
        return true;
      }

      const hasIds = (values.deliverymanIds?.length ?? 0) > 0;
      const hasCsv = !!values.deliverymanCsv;

      if (hasIds || hasCsv) {
        return true;
      }

      return this.createError({
        path: 'deliverymanIds',
        message: requiredErrorMessage,
      });
    },
  )
  .test('end-after-start', function validatePeriod(values) {
    const { startDate, startTime, endDate, endTime } = values || {};

    if (!startDate || !startTime || !endDate || !endTime) {
      return true;
    }

    const start = combineDateAndTime(startDate, startTime);
    const end = combineDateAndTime(endDate, endTime);

    if (end > start) {
      return true;
    }

    return new ValidationError([
      this.createError({
        path: 'endDate',
        message: MISSION_END_BEFORE_START_MESSAGE,
      }),
      this.createError({
        path: 'endTime',
        message: MISSION_END_BEFORE_START_MESSAGE,
      }),
    ]);
  })
  .test(
    'objectives-and-rewards-progression',
    function validateObjectivesAndRewardsProgression(values) {
      const errors: ValidationError[] = [];
      const {
        objective01,
        objective02,
        objective03,
        reward01,
        reward02,
        reward03,
      } = values || {};

      const objectiveValues = [
        parseObjectiveValue(objective01),
        parseObjectiveValue(objective02),
        parseObjectiveValue(objective03),
      ];
      const rewardValues = [
        parseMoneyValue(reward01),
        parseMoneyValue(reward02),
        parseMoneyValue(reward03),
      ];

      if (
        objectiveValues[1] !== null &&
        objectiveValues[0] !== null &&
        objectiveValues[1] <= objectiveValues[0]
      ) {
        errors.push(
          this.createError({
            path: 'objective02',
            message: MISSION_OBJECTIVE_GREATER_MESSAGE,
          }),
        );
      }

      if (
        objectiveValues[2] !== null &&
        objectiveValues[1] !== null &&
        objectiveValues[2] <= objectiveValues[1]
      ) {
        errors.push(
          this.createError({
            path: 'objective03',
            message: MISSION_OBJECTIVE_GREATER_MESSAGE,
          }),
        );
      }

      if (
        rewardValues[1] !== null &&
        rewardValues[0] !== null &&
        rewardValues[1] <= rewardValues[0]
      ) {
        errors.push(
          this.createError({
            path: 'reward02',
            message: MISSION_REWARD_GREATER_MESSAGE,
          }),
        );
      }

      if (
        rewardValues[2] !== null &&
        rewardValues[1] !== null &&
        rewardValues[2] <= rewardValues[1]
      ) {
        errors.push(
          this.createError({
            path: 'reward03',
            message: MISSION_REWARD_GREATER_MESSAGE,
          }),
        );
      }

      if (!errors.length) {
        return true;
      }

      return new ValidationError(errors);
    },
  );

export const CREATE_MISSION_FORM_DEFAULT_VALUES = {
  startDate: undefined,
  startTime: undefined,
  endDate: undefined,
  endTime: undefined,
  targetAudience: undefined,
  cityIds: [],
  deliverymanIds: [],
  deliverymanCsv: null,
  missionName: '',
  budget: '',
  objective01: '',
  objective02: '',
  objective03: '',
  reward01: '',
  reward02: '',
  reward03: '',
  missionTitle: '',
  missionDescription: '',
};

export enum EMissionStatus {
  FINISHED = 'FINISHED',
  ACTIVE = 'ACTIVE',
  CANCELED = 'CANCELED',
  PENDING = 'PENDING',
}

export const TRANSLATE_MISSION_STATUS: Record<EMissionStatus, string> = {
  [EMissionStatus.FINISHED]: 'Finalizada',
  [EMissionStatus.ACTIVE]: 'Em progresso',
  [EMissionStatus.CANCELED]: 'Cancelada',
  [EMissionStatus.PENDING]: 'Agendada',
};

export enum ENUM_MISSION_CHANGE_LOG_ACTION {
  CREATED = 'CREATED',
  UPDATED = 'UPDATED',
  CANCELED = 'CANCELED',
}

export const MISSION_CHANGE_LOG_EDITABLE_FIELDS = [
  'startDate',
  'endDate',
  'name',
  'budget',
  'rewardLevels',
  'title',
  'description',
] as const;

export const MISSION_CHANGE_LOG_FIELD_LABEL: Record<string, string> = {
  name: 'Nome da missão',
  startDate: 'Data inicial',
  endDate: 'Data final',
  budget: 'Orçamento',
  title: 'Título',
  description: 'Descrição da missão',
  type: 'Público-alvo',
  cities: 'Municípios',
  deliverymen: 'Entregadores',
  rewardLevels: 'Níveis de premiação',
};

export const MISSION_CHANGE_LOG_ACTION_LABEL: Record<
  ENUM_MISSION_CHANGE_LOG_ACTION,
  string
> = {
  [ENUM_MISSION_CHANGE_LOG_ACTION.CREATED]: 'Criou a missão',
  [ENUM_MISSION_CHANGE_LOG_ACTION.UPDATED]: 'Atualizou os dados da missão',
  [ENUM_MISSION_CHANGE_LOG_ACTION.CANCELED]: 'Cancelou a missão',
};

export const MISSION_CHANGE_LOG_ACTION_ICON: Record<
  ENUM_MISSION_CHANGE_LOG_ACTION,
  IconDefinition
> = {
  [ENUM_MISSION_CHANGE_LOG_ACTION.CREATED]: faPlus,
  [ENUM_MISSION_CHANGE_LOG_ACTION.UPDATED]: faPen,
  [ENUM_MISSION_CHANGE_LOG_ACTION.CANCELED]: faX,
};

export const MISSION_STATUS_OPTIONS = Object.values(EMissionStatus).map(
  status => ({
    key: status,
    text: TRANSLATE_MISSION_STATUS[status],
    value: status,
  }),
);

export const MISSIONS_LIST_FORM_SCHEMA = object({
  cityId: string().optional(),
  missionName: string().optional(),
  status: string()
    .transform((_, originalValue) =>
      originalValue === '' || originalValue == null ? undefined : originalValue,
    )
    .oneOf(Object.values(EMissionStatus))
    .optional(),
  startDate: date().optional().nullable(),
  endDate: date().optional().nullable(),
}).test('end-after-start', function validatePeriod(values) {
  const { startDate, endDate } = values || {};

  if (!startDate || !endDate) {
    return true;
  }

  if (endDate >= startDate) {
    return true;
  }

  return new ValidationError([
    this.createError({
      path: 'endDate',
      message: MISSION_END_BEFORE_START_MESSAGE,
    }),
  ]);
});

export const MISSIONS_LIST_FORM_DEFAULT_VALUES = {
  cityId: '',
  missionName: '',
  status: undefined,
  startDate: undefined,
  endDate: undefined,
};

export const DELIVERY_AVERAGE_BY_DAY_BOX_OPTIONS: ChartOptions<'bar'> = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'x',

  animation: {
    delay(rawContext) {
      const context = rawContext as typeof rawContext & {
        dropped?: boolean;
      };

      if (
        context.type === 'data' &&
        context.mode === 'default' &&
        !context.dropped
      ) {
        context.dropped = true;
        return context.dataIndex * 100;
      }
      return 0;
    },
  },

  elements: {
    bar: {
      borderRadius: 4,
    },
  },
  scales: {
    y: {
      beginAtZero: false,
      afterDataLimits(scale) {
        scale.max = Math.ceil(scale.max / 10) * 10;
      },
      min: 106,
      display: false,
    },
    x: {
      grid: {
        display: false,
      },
      border: {
        color: colors.gray10,
      },
    },
  },

  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    tooltip: {
      displayColors: false,
    },
    datalabels: {
      font: {
        size: 12,
      },
      align: 'end',
      anchor: 'end',
      padding: {
        right: -6,
      },
    },
  },
};
